import { API } from '@/config/api';
import { ApiError } from '@/shared/api/ApiError';
import { apiClient } from '@/shared/api/apiClient';

import type {
  ReferencePlanDays,
  StrategyAction,
  StrategyActionAnswer,
  StrategyDay,
  StrategyPlan,
} from '../types';

/**
 * Starting a generation, and rewriting one action, are calls to the browser agent. A generation only starts
 * (the agent works in the background), but the shared client's thirty seconds would still cut a slow start
 * off; a rewrite is a live model call, and a retry is a whole LinkedIn search. The previous screen allowed
 * twenty seconds, one minute and a little over five minutes.
 */
const KICKOFF_TIMEOUT_MS = 60_000;
const REWRITE_TIMEOUT_MS = 120_000;
const RETRY_TIMEOUT_MS = 330_000;

/** The plan as one calendar. **Null** when generation was never started: a 404, which is an answer and not a failure. */
export async function fetchStrategyPlan(): Promise<StrategyPlan | null> {
  try {
    return await apiClient.get<StrategyPlan>(API.linkedinAgentStrategy);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** Starts a generation; `startDate` is day 1, tomorrow when left out. Answers at once, the plan is read after. */
export async function generateStrategy(startDate?: string): Promise<void> {
  await apiClient.post<unknown>(API.linkedinAgentStrategyGenerate, startDate ? { startDate } : {}, {
    timeout: KICKOFF_TIMEOUT_MS,
  });
}

/** Replans only the days that are left, from tomorrow. Answers at once, the plan is read after. */
export async function regenerateStrategy(): Promise<void> {
  await apiClient.post<unknown>(API.linkedinAgentStrategyRegenerate, undefined, {
    timeout: KICKOFF_TIMEOUT_MS,
  });
}

/** Plans the next 30 days, starting the day after the current plan ends. Answers at once, the plan is read after. */
export async function renewStrategy(): Promise<void> {
  await apiClient.post<unknown>(API.linkedinAgentStrategyRenew, undefined, {
    timeout: KICKOFF_TIMEOUT_MS,
  });
}

/** Writes the requirement of the person for one day. */
export async function setDayNote(date: string, note: string): Promise<StrategyDay> {
  return apiClient.put<StrategyDay>(API.linkedinAgentStrategyDayNote(date), { note });
}

/** Merges the parameters into those of one action and/or annotates it. */
export async function editDayAction(
  date: string,
  actionId: string,
  params: Record<string, unknown>,
  note: string | undefined,
): Promise<StrategyAction> {
  return apiClient.put<StrategyAction>(API.linkedinAgentStrategyAction(date, actionId), {
    params,
    ...(note ? { note } : {}),
  });
}

/** Rejects one action, or with `regenerate` has the model write a replacement guided by the reason. */
export async function rejectDayAction(
  date: string,
  actionId: string,
  note: string,
  regenerate: boolean,
): Promise<StrategyAction> {
  return apiClient.post<StrategyAction>(
    API.linkedinAgentStrategyActionReject(date, actionId),
    { note, regenerate },
    { timeout: REWRITE_TIMEOUT_MS },
  );
}

/** Has the model rewrite one action, guided by the instruction, or by the note of the day. */
export async function regenerateDayAction(
  date: string,
  actionId: string,
  instruction?: string,
): Promise<StrategyAction> {
  return apiClient.post<StrategyAction>(
    API.linkedinAgentStrategyActionRegenerate(date, actionId),
    instruction ? { instruction } : {},
    { timeout: REWRITE_TIMEOUT_MS },
  );
}

/** Runs a failed search again, now: the whole LinkedIn search, so it takes minutes. */
export async function retryDayAction(
  date: string,
  actionId: string,
): Promise<StrategyActionAnswer> {
  return apiClient.post<StrategyActionAnswer>(
    API.linkedinAgentStrategyActionRetry(date, actionId),
    undefined,
    { timeout: RETRY_TIMEOUT_MS },
  );
}

/** The days of the reference plan. **Null** when none was generated: the server answers 204. */
export async function fetchReferencePlanDays(): Promise<ReferencePlanDays | null> {
  const answer = await apiClient.get<ReferencePlanDays | ''>(API.linkedinAgentReferencePlanDays);
  return answer === '' ? null : answer;
}
